// What the password strength meter shows (PR B1). Pure; tested in test/password.test.ts.
// Advice only: the server decides what is accepted (length, common / leaked
// passwords, the email in the password).

import { MIN_PASSWORD_LENGTH } from './password';

export type StrengthTone = 'bad' | 'warn' | 'ok' | 'wait';

export interface StrengthView {
  label: string;
  tone: StrengthTone;
  /** filled part of the bar, 1–4 of 4 */
  bars: number;
  tip: string;
}

/** `score` = zxcvbn's 0–4; `warning` / `suggestion` = its feedback (may be empty). */
export function strengthView(password: string, score: number, warning = '', suggestion = ''): StrengthView | null {
  if (!password) return null;
  if ([...password].length < MIN_PASSWORD_LENGTH) {
    return { label: 'Too short', tone: 'bad', bars: 1, tip: `Use at least ${MIN_PASSWORD_LENGTH} characters.` };
  }
  const s = Math.max(0, Math.min(4, Math.round(score)));
  const tip = (warning || suggestion).trim();
  if (s <= 1) return { label: 'Weak', tone: 'bad', bars: 1, tip: tip || 'Add another word or two.' };
  if (s === 2) return { label: 'Fair', tone: 'warn', bars: 2, tip: tip || 'Add another word to make it stronger.' };
  if (s === 3) return { label: 'Good', tone: 'ok', bars: 3, tip: '' };
  return { label: 'Strong', tone: 'ok', bars: 4, tip: '' };
}

/** Same rule as the server (password-policy.ts): the email's name (3+ letters) or the whole address in the password. */
export function emailInPassword(password: string, email: string): boolean {
  const pw = password.toLowerCase();
  const e = email.trim().toLowerCase();
  if (!e) return false;
  const local = e.includes('@') ? e.slice(0, e.lastIndexOf('@')) : e;
  return pw.includes(e) || (local.length >= 3 && pw.includes(local));
}

export const EMAIL_IN_PASSWORD: StrengthView = {
  label: 'Weak',
  tone: 'bad',
  bars: 1,
  tip: 'Don’t use your email address in your password.',
};

/** The server's answer for this password: refused with its reason, or allowed. */
export type ServerVerdict = { ok: true } | { ok: false; message: string };

/**
 * The server decides: a refused password shows "Not allowed" and the server's reason,
 * whatever the estimate said. An allowed one keeps the estimate (advice).
 */
export function withServerVerdict(view: StrengthView | null, verdict: ServerVerdict | null): StrengthView | null {
  if (!view || !verdict || verdict.ok) return view;
  return { label: 'Not allowed', tone: 'bad', bars: 1, tip: verdict.message };
}

/**
 * What the meter shows: while the server hasn't answered for this password yet
 * (`waiting`), "Checking…" (grey) instead of an estimate that may still turn into
 * "Not allowed" a moment later (the owner saw "Good" flip to red). Short passwords
 * and the email rule are decided here at once, so they never wait.
 */
export function meterView(view: StrengthView | null, verdict: ServerVerdict | null, waiting: boolean): StrengthView | null {
  if (!view) return null;
  if (verdict) return withServerVerdict(view, verdict);
  if (waiting && view.tone !== 'bad') return { label: 'Checking…', tone: 'wait', bars: view.bars, tip: '' };
  return view;
}
